import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import { Search as SearchIcon, FileText, Sparkles, TrendingUp, Loader2, ArrowRight, User, Calendar, MapPin, LayoutGrid, type LucideIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { useMediaCategories } from "@/hooks/useMediaCategories";
import { sections, moreMenuItems } from "@/lib/navigationConfig";

interface SearchArticle {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  category: string;
  publishedAt: string | null;
  similarity: number;
  matchedContent: string;
}

interface SearchPerson {
  id: string;
  firstName: string;
  lastName: string;
  profileSlug: string;
  profileImageUrl: string | null;
  company: string | null;
  title: string | null;
  role: string;
}

interface SearchEvent {
  id: string;
  title: string;
  slug: string;
  location: string | null;
  startDate: string | null;
  type: string;
}

interface SearchResults {
  articles: SearchArticle[];
  people: SearchPerson[];
  events: SearchEvent[];
  pages?: SearchPage[];
  aiAnswer: string | null;
  totalResults: number;
  searchMode: string;
}

interface SearchPage {
  routePath: string;
  pageTitle: string;
  similarity: number;
  matchedContent: string;
}

interface PopularSearch {
  query: string;
  count: number;
}

function useDebounce(value: string, delay: number): string {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

interface SearchablePage {
  label: string;
  href: string;
  section: string;
  icon?: LucideIcon;
  description?: string;
  keywords?: string[];
}

function buildSearchablePages(): SearchablePage[] {
  const seen = new Set<string>();
  const pages: SearchablePage[] = [];

  const add = (label: string, href: string, section: string, icon?: LucideIcon, description?: string, keywords?: string[]) => {
    if (seen.has(href) || href.includes("#")) return;
    seen.add(href);
    pages.push({ label, href, section, icon, description, keywords });
  };

  Object.entries(sections).forEach(([, config]) => {
    add(config.label, config.href, config.label, config.icon, config.description);
    config.subNavItems.forEach((item) => {
      add(item.label, item.href, config.label, item.icon, undefined, item.keywords);
    });
    config.hamburgerGroups.forEach((group) => {
      group.items.forEach((item) => {
        add(item.label, item.href, config.label, item.icon, item.description, item.keywords);
      });
    });
  });

  moreMenuItems.forEach((item) => {
    add(item.label, item.href, "More", item.icon, item.description, item.keywords);
  });

  return pages;
}

const searchablePages = buildSearchablePages();

export function Search({ expanded = false }: { expanded?: boolean }) {
  const { getCategoryLabel } = useMediaCategories();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState<"fast" | "ai">("fast");
  const [, setLocation] = useLocation();
  const debouncedQuery = useDebounce(searchQuery, 250);

  const filteredPages = useMemo(() => {
    if (!searchQuery || searchQuery.length < 1) return [];
    const words = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
    return searchablePages.filter((p) =>
      words.every((word) =>
        p.label.toLowerCase().includes(word) ||
        p.href.toLowerCase().includes(word) ||
        (p.description && p.description.toLowerCase().includes(word)) ||
        p.section.toLowerCase().includes(word) ||
        (p.keywords && p.keywords.some((kw) => kw.toLowerCase().includes(word)))
      )
    ).slice(0, 8);
  }, [searchQuery]);

  const { data: results, isLoading, isFetching } = useQuery<SearchResults>({
    queryKey: ["/api/search", debouncedQuery, searchMode, searchMode === "ai"],
    queryFn: async () => {
      if (!debouncedQuery.trim() || debouncedQuery.length < 2) {
        return { articles: [], people: [], events: [], aiAnswer: null, totalResults: 0, searchMode: "fast" };
      }
      const params = new URLSearchParams({
        q: debouncedQuery,
        mode: searchMode,
        includeAnswer: searchMode === "ai" ? "true" : "false",
      });
      const response = await fetch(`/api/search?${params}`);
      if (!response.ok) throw new Error("Search failed");
      return response.json();
    },
    enabled: open && debouncedQuery.length >= 2,
    staleTime: 10000,
    gcTime: 30000,
  });

  const { data: popularSearches } = useQuery<PopularSearch[]>({
    queryKey: ["/api/search/popular"],
    enabled: open && !searchQuery,
    staleTime: 60000,
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = useCallback((path: string) => {
    setOpen(false);
    setSearchQuery("");
    setSearchMode("fast");
    setLocation(path);
  }, [setLocation]);

  const handlePopularSearchClick = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const hasArticles = results && results.articles && results.articles.length > 0;
  const hasPeople = results && results.people && results.people.length > 0;
  const hasEvents = results && results.events && results.events.length > 0;
  const hasPages = filteredPages.length > 0;
  const aiPages = results?.pages?.filter((p) => !filteredPages.some((fp) => fp.href === p.routePath)) ?? [];
  const hasAiPages = searchMode === "ai" && aiPages.length > 0;
  const hasAnyResults = hasArticles || hasPeople || hasEvents || hasPages || hasAiPages;
  const isSearching = (isLoading || isFetching) && debouncedQuery.length >= 2;

  return (
    <>
      {expanded ? (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center w-full h-9 px-3 gap-2 rounded-md border border-input bg-muted/40 text-sm text-muted-foreground hover:bg-muted/60 transition-colors overflow-hidden"
          data-testid="button-search-expanded"
        >
          <SearchIcon className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left truncate">Search...</span>
          <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-background px-1.5 font-mono text-xs font-medium lg:flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setOpen(true)}
          data-testid="button-search"
        >
          <SearchIcon className="h-5 w-5" />
          <span className="sr-only">Search</span>
        </Button>
      )}
      <CommandDialog shouldFilter={false} open={open} onOpenChange={(val) => { setOpen(val); if (!val) { setSearchQuery(""); setSearchMode("fast"); } }}>
        <CommandInput
          placeholder="Search pages, articles, people, events..."
          value={searchQuery}
          onValueChange={setSearchQuery}
          data-testid="input-search"
        />
        <CommandList className="max-h-[500px]">
          {isSearching && (
            <div className="py-6 flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="search-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{searchMode === "ai" ? "Searching with AI..." : "Searching..."}</span>
            </div>
          )}

          {!searchQuery && (
            <CommandGroup heading="Quick Links" data-testid="search-group-quicklinks">
              {Object.values(sections).map((section) => {
                const SectionIcon = section.icon;
                return (
                  <CommandItem
                    key={`quick-${section.href}`}
                    value={`quick-${section.href}`}
                    onSelect={() => handleSelect(section.href)}
                    className="cursor-pointer"
                    data-testid={`search-quicklink-${section.label.toLowerCase()}`}
                  >
                    <SectionIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>{section.label}</span>
                    <span className="ml-2 text-xs text-muted-foreground truncate hidden sm:inline">{section.description}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {!searchQuery && popularSearches && popularSearches.length > 0 && (
            <CommandGroup heading="Popular Searches" data-testid="search-group-popular">
              {popularSearches.slice(0, 5).map((ps, idx) => (
                <CommandItem
                  key={`pop-${idx}`}
                  value={`popular-${ps.query}`}
                  onSelect={() => handlePopularSearchClick(ps.query)}
                  className="cursor-pointer"
                  data-testid={`search-popular-${idx}`}
                >
                  <TrendingUp className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span>{ps.query}</span>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {ps.count}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {hasPages && (
            <CommandGroup heading="Pages" data-testid="search-group-pages">
              {filteredPages.map((page) => {
                const PageIcon = page.icon || LayoutGrid;
                return (
                  <CommandItem
                    key={`page-${page.href}`}
                    value={`page-${page.href}`}
                    onSelect={() => handleSelect(page.href)}
                    className="cursor-pointer"
                    data-testid={`search-result-page-${page.href}`}
                  >
                    <PageIcon className="h-4 w-4 mr-3 flex-shrink-0 text-muted-foreground" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-medium text-sm">{page.label}</span>
                      {page.description && (
                        <span className="text-xs text-muted-foreground line-clamp-1">{page.description}</span>
                      )}
                    </div>
                    <Badge variant="secondary" className="text-xs ml-2">
                      {page.section}
                    </Badge>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {!isSearching && debouncedQuery.length >= 2 && !hasAnyResults && (
            <CommandEmpty data-testid="search-empty">
              No results found. Try different keywords.
            </CommandEmpty>
          )}

          {results?.aiAnswer && (
            <>
              <div className="p-4 mx-2 my-2 rounded-md bg-accent/30 border" data-testid="ai-answer">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium">AI Answer</span>
                </div>
                <p className="text-sm text-muted-foreground">{results.aiAnswer}</p>
              </div>
              <CommandSeparator />
            </>
          )}

          {hasAnyResults && searchMode === "fast" && (
            <div className="px-4 py-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full gap-2 text-purple-600"
                onClick={() => setSearchMode("ai")}
                data-testid="button-ai-search"
              >
                <Sparkles className="h-4 w-4" />
                Enhance with AI search
                <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          )}

          {hasPages && (hasArticles || hasAiPages) && <CommandSeparator />}

          {hasAiPages && (
            <>
              <CommandGroup heading={`Pages (${aiPages.length})`} data-testid="search-group-ai-pages">
                {aiPages.map((page) => (
                  <CommandItem
                    key={`ai-page-${page.routePath}`}
                    value={`ai-page-${page.routePath}`}
                    onSelect={() => handleSelect(page.routePath)}
                    className="cursor-pointer"
                    data-testid={`search-result-ai-page-${page.routePath}`}
                  >
                    <LayoutGrid className="h-4 w-4 mr-3 flex-shrink-0 text-purple-500" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-medium text-sm">{page.pageTitle}</span>
                      {page.matchedContent && (
                        <span className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                          {page.matchedContent}
                        </span>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs ml-2 text-purple-600 border-purple-200">
                      Page
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {!hasAiPages && hasPages && hasArticles && <CommandSeparator />}

          {hasArticles && (
            <CommandGroup heading={`Articles (${results!.articles.length})`} data-testid="search-group-articles">
              {results!.articles.map((article) => (
                <CommandItem
                  key={`article-${article.id}`}
                  value={`article-${article.id}-${article.slug}`}
                  onSelect={() => handleSelect(`/media/${article.slug}`)}
                  className="cursor-pointer"
                  data-testid={`search-result-article-${article.id}`}
                >
                  <div className="flex-shrink-0">
                    {article.coverImageUrl ? (
                      <img
                        src={article.coverImageUrl}
                        alt=""
                        className="h-10 w-14 rounded object-cover mr-3"
                      />
                    ) : (
                      <div className="h-10 w-14 rounded bg-muted mr-3 flex items-center justify-center">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium text-sm leading-tight line-clamp-2">{article.title}</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className="text-xs">
                        {getCategoryLabel(article.category)}
                      </Badge>
                      {article.similarity >= 0.5 && (
                        <span className="text-xs text-green-600">High match</span>
                      )}
                    </div>
                    {article.matchedContent && (
                      <span className="text-xs text-muted-foreground line-clamp-1 mt-1">
                        {article.matchedContent}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {hasPeople && (
            <>
              <CommandSeparator />
              <CommandGroup heading={`People (${results!.people.length})`} data-testid="search-group-people">
                {results!.people.map((person) => (
                  <CommandItem
                    key={`person-${person.id}`}
                    value={`person-${person.id}-${person.firstName}-${person.lastName}`}
                    onSelect={() => handleSelect(`/profile/${person.profileSlug || person.id}`)}
                    className="cursor-pointer"
                    data-testid={`search-result-person-${person.id}`}
                  >
                    <div className="flex-shrink-0">
                      {person.profileImageUrl ? (
                        <img
                          src={person.profileImageUrl}
                          alt=""
                          className="h-8 w-8 rounded-full object-cover mr-3"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-muted mr-3 flex items-center justify-center">
                          <User className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-medium text-sm">
                        {person.firstName} {person.lastName}
                      </span>
                      {(person.title || person.company) && (
                        <span className="text-xs text-muted-foreground line-clamp-1">
                          {person.title}{person.title && person.company ? " at " : ""}{person.company}
                        </span>
                      )}
                    </div>
                    <Badge variant="secondary" className="text-xs capitalize ml-2">
                      {person.role}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {hasEvents && (
            <>
              <CommandSeparator />
              <CommandGroup heading={`Events (${results!.events.length})`} data-testid="search-group-events">
                {results!.events.map((event) => (
                  <CommandItem
                    key={`event-${event.id}`}
                    value={`event-${event.id}-${event.slug}`}
                    onSelect={() => handleSelect(`/events/${event.slug}`)}
                    className="cursor-pointer"
                    data-testid={`search-result-event-${event.id}`}
                  >
                    <Calendar className="h-4 w-4 mr-3 flex-shrink-0 text-muted-foreground" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-medium text-sm line-clamp-1">{event.title}</span>
                      {event.location && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {event.location}
                        </span>
                      )}
                    </div>
                    <Badge variant="secondary" className="text-xs capitalize ml-2">
                      {event.type}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
